import { Icon, Icons, StepItemProps, Tooltip, Button, Span } from "@ir/ui";
import { IAddChargebreakdownHeaders, IShowPreviewCellProps, ShowPreviewValueProps } from "../interface/AddChargebreakdownTypes";
import { DateAdapter } from "@/infrastructure/adapters/dayJs";
import { NumberCheckAdapter } from "@/infrastructure/adapters/number";
import { cn } from "@/infrastructure/adapters/classnames";

const maxStringLength = 100;
export const acceptedDateFormats = ["MM/DD/YYYY", "MM-DD-YYYY", "MM/DD/YY", "MM-DD-YY"];
export const maxNumber = 100000000; // 100 million
export const minNumber = -100000000; // -100 million
export const maxChargebreakdownNumberCharacters = maxStringLength;
export const maxChargebreakdownInvoiceNumberCharacters = maxStringLength;

const numberCheck = new NumberCheckAdapter();
const dateAdapter = new DateAdapter();

// Add Chargebreakdown Headers
export const addChargebreakdownHeadersMasterRaw: IAddChargebreakdownHeaders[] = [
  {
    key: "upcItemNumber",
    targetedHeader: "UPC",
    findDictionary: ["UPC", "upc", "upc item", "upc-item", "upcItem", "upc_item"],
  },

  {
    key: "itemNumber",
    targetedHeader: "Item Number",
    findDictionary: ["Item Number", "item number", "itemNumber", "itemnumber", "item-number", "item_number"],
  },

  {
    key: "notes",
    targetedHeader: "Notes",
    findDictionary: ["notes", "note", "note description", "noteDescription", "note-description", "note_description"],
  },
  {
    key: "poDate",
    targetedHeader: "PO Date",
    findDictionary: ["po date", "poDate", "po-date", "po_date", "po date", "po-date"],
  },
  {
    key: "quantity",
    targetedHeader: "Quantity",
    findDictionary: ["quantity", "quantity count", "quantityCount", "quantity-count", "quantity_count"],
  },
  {
    key: "allowance",
    targetedHeader: "Allowance",
    findDictionary: ["allowance", "allowance amount", "allowanceAmount", "allowance-amount", "allowance_amount"],
  },
  {
    key: "packSize",
    targetedHeader: "Pack Size",
    findDictionary: ["pack size", "packSize", "pack-size", "pack_size"],
  },
  {
    key: "poNumber",
    targetedHeader: "PO Number",
    findDictionary: ["po number", "poNumber", "po-number", "po_number"],
  },
  {
    key: "shipDate",
    targetedHeader: "Ship Date",
    findDictionary: ["ship date", "shipDate", "ship-date", "ship_date"],
  },
  {
    key: "unitCost",
    targetedHeader: "Unit Cost",
    findDictionary: ["unit cost", "unitCost", "unit-cost", "unit_cost"],
  },
  {
    key: "expiryDate",
    targetedHeader: "Expiry Date",
    findDictionary: ["expiry date", "expiryDate", "expiry-date", "expiry_date"],
  },

  {
    key: "refInvoice",
    targetedHeader: "Ref. Invoice",
    findDictionary: [
      "Ref. invoice",
      "refInvoice",
      "ref-invoice",
      "ref_invoice",
      "ref invoice number",
      "refInvoiceNumber",
      "ref-invoice-number",
      "ref_invoice_number",
    ],
  },
  {
    key: "invoiceDate",
    targetedHeader: "Invoice Date",
    findDictionary: ["invoice date", "invoiceDate", "invoice-date", "invoice_date"],
  },
  {
    key: "totalAmount",
    targetedHeader: "Total Amount",
    findDictionary: ["total amount", "totalAmount", "total-amount", "total_amount"],
  },
  {
    key: "receivedDate",
    targetedHeader: "Received Date",
    findDictionary: ["received date", "receivedDate", "received-date", "received_date"],
  },
  {
    key: "retailerName",
    targetedHeader: "Retailer Name",
    findDictionary: ["retailer name", "retailerName", "retailer-name", "retailer_name"],
  },
  {
    key: "invoiceAmount",
    targetedHeader: "Invoice Amount",
    findDictionary: ["invoice amount", "invoiceAmount", "invoice-amount", "invoice_amount"],
  },
  {
    key: "orderQuantity",
    targetedHeader: "Order Quantity",
    findDictionary: ["order quantity", "orderQuantity", "order-quantity", "order_quantity"],
  },
  {
    key: "costDifference",
    targetedHeader: "Cost Difference",
    findDictionary: ["cost difference", "costDifference", "cost-difference", "cost_difference"],
  },
  {
    key: "retailerNumber",
    targetedHeader: "Retailer Number",
    findDictionary: ["retailer number", "retailerNumber", "retailer-number", "retailer_number"],
  },

  {
    key: "itemDescription",
    targetedHeader: "Item Description",
    findDictionary: ["item description", "itemDescription", "item-description", "item_description"],
  },
  {
    key: "receivedUnitCost",
    targetedHeader: "Received Unit Cost",
    findDictionary: ["received unit cost", "receivedUnitCost", "received-unit-cost", "received_unit_cost"],
  },
  {
    key: "distributionCenter",
    targetedHeader: "Distribution Center",
    findDictionary: ["distribution center", "distributionCenter", "distribution-center", "distribution_center", "DC"],
  },
  {
    key: "quantityDifference",
    targetedHeader: "Quantity Difference",
    findDictionary: ["quantity difference", "quantityDifference", "quantity-difference", "quantity_difference"],
  },
  {
    key: "discount",
    targetedHeader: "Discount",
    findDictionary: ["discount", "discount amount", "discountAmount", "discount-amount", "discount_amount"],
  },
  {
    key: "divisionName",
    targetedHeader: "Division Name",
    findDictionary: ["division name", "division #", "division#", "division no.", "division no", "divisionName", "division-name", "division_name"],
  },
  {
    key: "divisionNumber",
    targetedHeader: "Division Number",
    findDictionary: ["division number", "divisionNumber", "division-number", "division_number"],
  },
];

// Add Chargebreakdown Steps
export const addChargebreakdownStepsMasterRaw: StepItemProps[] = [
  {
    key: "upload-chargebreakdown",
    label: "Upload",
    icon: <Icon name={Icons.File} />,
    isReadOnly: true,
  },
  {
    key: "map-chargebreakdown",
    label: "Map",
    icon: <Icon name={Icons.Flash} />,
    isReadOnly: true,
  },
  {
    key: "verify-chargebreakdown",
    label: "Verify",
    icon: <Icon name={Icons.Check} />,
    isReadOnly: true,
  },
];

export const maxLengthValidity = (value: string | null, maxLength: number, placeholder: string = "String"): string[] | null => {
  if (value && value.length > maxLength) {
    return [`${placeholder} cannot exceed ${maxLength} characters.`];
  }
  return null;
};

export const dateValidity = (date: string, placeholder: string = "Date", required: boolean = true): string[] | null => {
  if (!required && (!date || date === "")) {
    return null;
  }

  if (!date || date === "") {
    return [`Please enter a valid ${placeholder?.toLowerCase()}.`];
  }

  // Date cannot be invalid date
  if (!dateAdapter.isValid(date)) {
    return [`${placeholder} must be in ${acceptedDateFormats.join(" or ")} format.`];
  }

  // Date must be in the correct format (MM/DD/YYYY, MM-DD-YYYY, MM/DD/YY, MM-DD-YY)
  const dateRegexMatch = /^\d{2}([/-])\d{2}\1(\d{2}|\d{4})$/;

  if (!dateRegexMatch.test(date)) {
    return [`${placeholder} must be in ${acceptedDateFormats.join(" or ")} format.`];
  }
  const [month, day, year] = date.includes("/") ? date.split("/") : date.split("-");
  const dateObject = new Date(Number(year), Number(month) - 1, Number(day));
  if (Number.isNaN(dateObject.getTime())) {
    return [`Please enter a valid ${placeholder}.`];
  }

  // Date cannot be before 1900-01-01
  if (dateAdapter.isBefore(date, "01/01/1900")) {
    return [`${placeholder} cannot be before 01/01/1900.`];
  }
  // Date cannot be in the future
  if (dateAdapter.isAfter(date, dateAdapter.getCurrentDate().toISOString())) {
    return [`${placeholder} cannot be in the future.`];
  }
  return null;
};

export const isValidNumber = (value: string | number): boolean => {
  if (value === null || value === undefined || value === "") {
    return false;
  }
  if (!numberCheck.isNumber(value.toString())) {
    return false;
  }
  return true;
};

export const isPositiveNumber = (value: string | number): boolean => {
  if (!isValidNumber(value)) {
    return false;
  }
  return numberCheck.isPositiveNumber(value.toString());
};

export const numberValidity = (value: string | number, placeholder: string = "Number"): string[] | null => {
  if (value === null || value === undefined || value === "") {
    return [`Please enter a valid ${placeholder}.`];
  }
  if (!isValidNumber(value)) {
    return [`${placeholder} must be a number.`];
  }
  if (!isNumberBetween(value.toString(), minNumber, maxNumber)) {
    return [`${placeholder} must be between ${minNumber} and ${maxNumber}.`];
  }
  return null;
};

export const numberValidityIfValueExist = (value: string | number | null, placeholder: string = "Number"): string[] | null => {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  if (!isValidNumber(value)) {
    return [`${placeholder} must be a number.`];
  }
  if (!isNumberBetween(value.toString(), minNumber, maxNumber)) {
    return [`${placeholder} must be between ${minNumber} and ${maxNumber}.`];
  }
  return null;
};

export const stringValidity = (value: string | number | null, placeholder: string = "String"): string[] | null => {
  let _value = value;
  if (value && typeof value === "number") {
    _value = value.toString();
  }
  // check if it is not too long
  if (_value && typeof _value === "string" && _value.length > maxChargebreakdownNumberCharacters) {
    return [`${placeholder} cannot exceed ${maxChargebreakdownNumberCharacters} characters.`];
  }
  return null;
};

export const convertToTwoDecimalPlacesNumber = (value: string | number): number => {
  if (!isValidNumber(value)) {
    return 0;
  }
  return Number(Number(value).toFixed(2));
};

export const isNumberBetween = (value: string | number, min: number, max: number): boolean => {
  if (!isValidNumber(value)) {
    return false;
  }
  return numberCheck.isNumberBetween(value.toString(), min, max);
};

export const isError = (errors: string[] | null): boolean => {
  if (!errors || errors === null || errors.length === 0) {
    return false;
  }
  return true;
};

const ShowPreviewErrors = (errors: string[] | null) => {
  if (!errors || !isError(errors) || errors.length === 0) {
    return null;
  }
  let errorContent: React.ReactNode = "";
  if (errors.length === 1) {
    errorContent = errors[0];
  } else {
    errorContent = (
      <ol>
        {errors.map(error => (
          <li key={error}>{error}</li>
        ))}
      </ol>
    );
  }
  return (
    <Tooltip content={errorContent}>
      <Button noStyle>
        <Icon name={Icons.AlertCircle} size="small" color="error" />
      </Button>
    </Tooltip>
  );
};

const ShowPreviewValue = (value: ShowPreviewValueProps) => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }
  if (typeof value === "string" && value.trim().toLowerCase() === "nan") {
    return "-";
  }
  return value;
};

export const ShowPreviewCell: React.FC<IShowPreviewCellProps> = ({ value, errors, displayValueClassName, children }) => {
  const maxCharacters = 20;
  const currentValue = ShowPreviewValue(value)?.toString();
  let displayValue: React.ReactNode = currentValue;
  if (currentValue && currentValue.length > maxCharacters) {
    displayValue = (
      <Tooltip content={currentValue}>
        <Button style={{ textAlign: "left" }} className="preview-value-button-tooltip" noStyle>
          {currentValue.substring(0, maxCharacters)}...
        </Button>
      </Tooltip>
    );
  }
  return (
    <Span className={cn(isError(errors) && "preview-error")}>
      {children ? (
        <>
          {children}
          {ShowPreviewErrors(errors)}
        </>
      ) : (
        <>
          <Span className={cn("preview-value", displayValueClassName)} style={{ wordBreak: "break-word", whiteSpace: "normal" }}>
            {displayValue}
          </Span>
          {ShowPreviewErrors(errors)}
        </>
      )}
    </Span>
  );
};
